// ====================================================================
// EDIT LAYER — serializable world-edit deltas (authoring persistence)
// ====================================================================
// The village is built deterministically from procedural code on every load,
// so we never snapshot the whole world. Instead the editor keeps a small DELTA
// — terrain height overrides, props it added, ids of procedural props it
// removed, and structure/sign transforms — and replays it AFTER the procedural
// build. Reload re-runs the same deterministic build and re-applies the delta;
// nothing double-adds, and "reset" is simply dropping the layer.
//
// This module is intentionally dumb: it only holds the schema, persists it to
// localStorage (debounced, behind try/catch so the no-build page and private
// mode never throw), and serializes to/from JSON. The editor (src/editor/
// worldEdit.js) owns *applying* it, because that needs live runtime handles.
//
// It is imported only from the editor path, but it is written core-safe (no
// build-flag or DOM assumptions, no top-level side effects) so importing it can
// never break a kid build.

// v2 (bumped from v1): added signsAdded + structuresAdded (editor-created
// objects, which v1 could only MOVE, not create) and an optional `text` on the
// `signs` map so text edits to procedural signs persist too. v1→v2 is a clean
// break — the new KEY means old v1 autosaves are simply ignored (drop, don't
// migrate); the committable baked baseline (world-baked.json) is the durable
// artifact, so losing a browser's stale working copy is acceptable.
export const EDIT_LAYER_VERSION = 2;
const KEY = 'chockytown.editLayer.v2';

// terrain:         { "<latticeIndex>": deltaHeight, ... }   (sparse, from heightfield.getHeightDeltas)
// propsAdded:      [ { eid, kind:'prop'|'tree', type, x, z, scale, yaw, ...rolled }, ... ]
// propsRemoved:    [ "<procedural prop id>", ... ]
// structures:      { "<name>": { pos:[x,y,z], rot:[x,y,z], scale:[x,y,z] }, ... }   (move existing)
// structuresAdded: [ { id, type, x, z, w, d, wallH, wallColor, roofColor, withChimney, decor, name, pos?, rot?, scale? }, ... ]
// signs:           { "<id|name>": { x, y, z, yaw, text? }, ... }   (move / retext existing)
// signsAdded:      [ { id, text, x, y, z, yaw }, ... ]
export function emptyLayer() {
  return {
    v: EDIT_LAYER_VERSION,
    terrain: {}, propsAdded: [], propsRemoved: [],
    structures: {}, structuresAdded: [], signs: {}, signsAdded: [],
  };
}

function normalize(layer) {
  return {
    v: EDIT_LAYER_VERSION,
    terrain: layer.terrain || {},
    propsAdded: layer.propsAdded || [],
    propsRemoved: layer.propsRemoved || [],
    structures: layer.structures || {},
    structuresAdded: layer.structuresAdded || [],
    signs: layer.signs || {},
    signsAdded: layer.signsAdded || [],
  };
}

let _saveTimer = null;

// Debounced autosave. Pass immediate=true to flush synchronously (e.g. on a
// manual Save or before unload).
export function saveLayer(layer, immediate = false) {
  const write = () => {
    _saveTimer = null;
    try { localStorage.setItem(KEY, JSON.stringify(layer)); } catch {}
  };
  if (_saveTimer) { clearTimeout(_saveTimer); _saveTimer = null; }
  if (immediate) write();
  else _saveTimer = setTimeout(write, 800);
}

// Returns a normalized layer, or null if absent / unparseable / a stale schema
// version (in which case the caller should just run the clean procedural world).
export function loadAutosave() {
  let raw = null;
  try { raw = localStorage.getItem(KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const layer = JSON.parse(raw);
    if (!layer || layer.v !== EDIT_LAYER_VERSION) return null;
    return normalize(layer);
  } catch { return null; }
}

export function clearAutosave() {
  if (_saveTimer) { clearTimeout(_saveTimer); _saveTimer = null; }
  try { localStorage.removeItem(KEY); } catch {}
}

export function serialize(layer) { return JSON.stringify(layer, null, 2); }

// Throws on a bad/unsupported document so the importer can surface an error.
export function parse(json) {
  const layer = JSON.parse(json);
  if (!layer || typeof layer !== 'object') throw new Error('Not an edit-layer object');
  if (layer.v !== EDIT_LAYER_VERSION) throw new Error(`Unsupported edit-layer version: ${layer.v}`);
  return normalize(layer);
}
